import { MixedRouteSDK, Trade as RouterTrade, TPool } from 'astra-router-sdk'
import { Currency, CurrencyAmount, Amber, Token, TradeType } from 'astra-sdk-core'
import { Pair, Route as ClassicRoute } from 'astra-classic-sdk'
import { Pool, Route as CLRoute, FeeAmount } from 'astra-cl-sdk-dev'
import { BigNumber } from 'ethers'
import { AMB_ADDRESS, E_AMB_ADDRESS } from './constants'

export type TokenInRoute = {
  address: string
  chainId: number
  symbol: string
  decimals: string
  name?: string
  buyFeeBps?: string
  sellFeeBps?: string
}

export enum PoolType {
  ClassicPool = 'classic-pool',
  CLPool = 'cl-pool',
}

export type ClassicReserve = {
  token: TokenInRoute
  quotient: string
}

export type ClassicPoolInRoute = {
  type: PoolType.ClassicPool
  address?: string
  tokenIn: TokenInRoute
  tokenOut: TokenInRoute
  reserve0: ClassicReserve
  reserve1: ClassicReserve
  amountIn?: string
  amountOut?: string
}

export type CLPoolInRoute = {
  type: PoolType.CLPool
  address?: string
  tokenIn: TokenInRoute
  tokenOut: TokenInRoute
  sqrtRatioX96: string
  liquidity: string
  tickCurrent: string
  fee: string
  amountIn?: string
  amountOut?: string
}

export type PartialClassicQuote = {
  // We need tokenIn/Out to support native currency
  tokenIn: string
  tokenOut: string
  tradeType: TradeType
  route: Array<(CLPoolInRoute | ClassicPoolInRoute)[]>
}

interface RouteResult {
  routecl: CLRoute<Currency, Currency> | null
  routeclassic: ClassicRoute<Currency, Currency> | null
  mixedRoute: MixedRouteSDK<Currency, Currency> | null
  inputAmount: CurrencyAmount<Currency>
  outputAmount: CurrencyAmount<Currency>
}

export const isNativeCurrency = (address: string) =>
  address.toLowerCase() === AMB_ADDRESS.toLowerCase() || address.toLowerCase() === E_AMB_ADDRESS.toLowerCase()

// Helper class to convert routing-specific quote entities to RouterTrade entities
// the returned RouterTrade can then be used to build the AstraTrade entity in this package
export class RouterTradeAdapter {
  // Generate a RouterTrade using fields from a classic quote response
  static fromClassicQuote(quote: PartialClassicQuote) {
    const { route, tokenIn, tokenOut } = quote

    if (!route) throw new Error('Expected route to be present')
    if (!route.length) throw new Error('Expected there to be at least one route')
    if (route.some((r) => !r.length)) throw new Error('Expected all routes to have at least one pool')
    const firstRoute = route[0]

    const tokenInData = firstRoute[0].tokenIn
    const tokenOutData = firstRoute[firstRoute.length - 1].tokenOut

    if (!tokenInData || !tokenOutData) throw new Error('Expected both tokenIn and tokenOut to be present')
    if (tokenInData.chainId !== tokenOutData.chainId)
      throw new Error('Expected tokenIn and tokenOut to be have same chainId')

    const parsedCurrencyIn = RouterTradeAdapter.toCurrency(isNativeCurrency(tokenIn), tokenInData)
    const parsedCurrencyOut = RouterTradeAdapter.toCurrency(isNativeCurrency(tokenOut), tokenOutData)

    const typedRoutes: RouteResult[] = route.map((subRoute) => {
      const rawAmountIn = subRoute[0].amountIn
      const rawAmountOut = subRoute[subRoute.length - 1].amountOut

      if (!rawAmountIn || !rawAmountOut) {
        throw new Error('Expected both raw amountIn and raw amountOut to be present')
      }

      const inputAmount = CurrencyAmount.fromRawAmount(parsedCurrencyIn, rawAmountIn)
      const outputAmount = CurrencyAmount.fromRawAmount(parsedCurrencyOut, rawAmountOut)

      const isOnlyClassic = RouterTradeAdapter.isVersionedRoute<ClassicPoolInRoute>(PoolType.ClassicPool, subRoute)
      const isOnlyCL = RouterTradeAdapter.isVersionedRoute<CLPoolInRoute>(PoolType.CLPool, subRoute)

      return {
        routecl: isOnlyCL
          ? new CLRoute(
              (subRoute as CLPoolInRoute[]).map(RouterTradeAdapter.toPool),
              parsedCurrencyIn,
              parsedCurrencyOut
            )
          : null,
        routeclassic: isOnlyClassic
          ? new ClassicRoute(
              (subRoute as ClassicPoolInRoute[]).map(RouterTradeAdapter.toPair),
              parsedCurrencyIn,
              parsedCurrencyOut
            )
          : null,
        mixedRoute:
          !isOnlyCL && !isOnlyClassic
            ? new MixedRouteSDK(
                subRoute.map(RouterTradeAdapter.toPoolOrPair) as TPool[],
                parsedCurrencyIn,
                parsedCurrencyOut
              )
            : null,
        inputAmount,
        outputAmount,
      }
    })

    return new RouterTrade({
      classicRoutes: typedRoutes
        .filter((route) => route.routeclassic)
        .map((route) => ({
          routeclassic: route.routeclassic as ClassicRoute<Currency, Currency>,
          inputAmount: route.inputAmount,
          outputAmount: route.outputAmount,
        })),
      clRoutes: typedRoutes
        .filter((route) => route.routecl)
        .map((route) => ({
          routecl: route.routecl as CLRoute<Currency, Currency>,
          inputAmount: route.inputAmount,
          outputAmount: route.outputAmount,
        })),
      mixedRoutes: typedRoutes
        .filter((route) => route.mixedRoute)
        .map((route) => ({
          mixedRoute: route.mixedRoute as MixedRouteSDK<Currency, Currency>,
          inputAmount: route.inputAmount,
          outputAmount: route.outputAmount,
        })),
      tradeType: quote.tradeType,
    })
  }

  private static toCurrency(isNative: boolean, token: TokenInRoute): Currency {
    if (isNative) {
      return Amber.onChain(token.chainId)
    }
    return this.toToken(token)
  }

  private static toPoolOrPair = (pool: CLPoolInRoute | ClassicPoolInRoute): Pool | Pair => {
    return pool.type === PoolType.CLPool ? RouterTradeAdapter.toPool(pool) : RouterTradeAdapter.toPair(pool)
  }

  private static toToken(token: TokenInRoute): Token {
    const { chainId, address, decimals, symbol, buyFeeBps, sellFeeBps } = token
    return new Token(
      chainId,
      address,
      parseInt(decimals.toString()),
      symbol,
      /* name */ undefined,
      false,
      buyFeeBps ? BigNumber.from(buyFeeBps) : undefined,
      sellFeeBps ? BigNumber.from(sellFeeBps) : undefined
    )
  }

  private static toPool({ fee, sqrtRatioX96, liquidity, tickCurrent, tokenIn, tokenOut }: CLPoolInRoute): Pool {
    return new Pool(
      RouterTradeAdapter.toToken(tokenIn),
      RouterTradeAdapter.toToken(tokenOut),
      parseInt(fee) as FeeAmount,
      sqrtRatioX96,
      liquidity,
      parseInt(tickCurrent)
    )
  }

  private static toPair = ({ reserve0, reserve1 }: ClassicPoolInRoute): Pair => {
    return new Pair(
      CurrencyAmount.fromRawAmount(RouterTradeAdapter.toToken(reserve0.token), reserve0.quotient),
      CurrencyAmount.fromRawAmount(RouterTradeAdapter.toToken(reserve1.token), reserve1.quotient)
    )
  }

  private static isVersionedRoute<T extends ClassicPoolInRoute | CLPoolInRoute>(
    type: PoolType,
    route: (CLPoolInRoute | ClassicPoolInRoute)[]
  ): route is T[] {
    return route.every((pool) => pool.type === type)
  }
}
